import { getDefaultStore } from 'jotai';
import { accessTokenAtom, authErrorAtom, authInProgressAtom } from '../atoms/authAtom';
import { getGigyaToken, isGigyaLoggedIn } from './gigya';
import axios from 'axios';

// Add request interceptor to include locale header
axios.interceptors.request.use((config) => {
  const locale = window.fsl_locale || 'en-us';
  config.headers['X-FSL-Locale'] = locale;
  return config;
});

export interface JwtResponse {
  token: string;
  expiresIn: number;
  sold_to: string;
  role: string;
  email?: string;
  preferredShipTo?: string | null;
}

let initializationPromise: Promise<void> | null = null;

export function initializeAuth(): Promise<void> {
  if (!initializationPromise) {
    const store = getDefaultStore();
    store.set(authInProgressAtom, true);

    initializationPromise = (async () => {
      try {
        const gigyaLoggedIn = await isGigyaLoggedIn();
        if (!gigyaLoggedIn) {
          store.set(authErrorAtom, 'User not logged in to Gigya');
          return;
        }

        const gigyaToken = await getGigyaToken();
        const authResponse = await exchangeGigyaTokenForJWT(gigyaToken);

        store.set(accessTokenAtom, authResponse.token);
        store.set(authErrorAtom, null);
      } catch (error) {
        console.error('[AuthService] Initialization failed:', error);
        const errorMessage = error instanceof Error ? error.message : 'Authentication failed';
        store.set(authErrorAtom, errorMessage);
        store.set(accessTokenAtom, undefined);
      } finally {
        store.set(authInProgressAtom, false);
      }
    })();
  }
  return initializationPromise;
}

export function getAuthInitializationPromise(): Promise<void> | null {
  return initializationPromise;
}

export async function exchangeGigyaTokenForJWT(gigyaToken: string): Promise<JwtResponse> {
  const baseUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:3002/api';
  const response = await axios.post(
    '/auth/gigya',
    { gigyaToken },
    { baseURL: baseUrl.replace('/api', '') }
  );
  return response.data;
}
